import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider as AntDivider, Progress, Radio, RadioChangeEvent } from "antd";
import ColorPicker from "Components/UI/ColorPicker";
import { 
  CardSubTitle,
  CardTitle,
  Divider, 
  ImgWrapper, 
  InfoText, 
  InfoTextBold, 
  MainWrapperLeft, 
  MainWrapperRight,
  SvgCard,
} from "./styles";
import ButtonsAction from "../ButtonsAction";
import { useSelector } from "react-redux";
import { 
  getDefaultInfoTheme, 
  getDomain, 
  getDomainLoading, 
  getImgUploadState, 
  getTheme,
} from "Store/MultiDomain/MultiDomain.selector";
import UploadCropImg from "Components/UI/UploadCropImg";
import { MultiDomainActions } from "Store/MultiDomain/MultiDomain.action";
import { 
  ITenantPersonalizationUpdateData, 
  IUpdateTenantPersonalizationRequest,
  TypePathUpdateData,
} from "Data/interfaces/MultiDomain/IUpdateMultiDomainPersonalization";
import { Dispatch } from "redux";
import { RcFile } from "antd/es/upload/interface";
import { 
  IUploadPersonalization,
} from "Data/interfaces/MultiDomain/IUploadPersonalization";
import { loadImage } from "Utils/MultidomailUtils";
import { AppearanceBackgoundImage } from "./AppearanceBackgoundImage";

enum RadioOptions {
  Coordly,
  Custom,
}

interface IAppearanceTab {
  dispatch: Dispatch<any>;
  tenantFk?: number;
}

const AppearanceTab: FC<IAppearanceTab> = ({
  dispatch,
  tenantFk,
}) => {
  const { t } = useTranslation('personalization');
  const theme = useSelector(getTheme);
  const domain = useSelector(getDomain);
  const defaultInfoTheme = useSelector(getDefaultInfoTheme);
  const loading = useSelector(getDomainLoading);
  const imgUploadState = useSelector(getImgUploadState);

  const [radioOpt, setRadioOpt] = useState<RadioOptions>(0);
  const [newColor, setNewColor] = useState(theme.colors.primary.primary);
  const [newImg, setNewImg] = useState<string | undefined>(undefined);
  const [currentFile, setCurrentFile] = useState<RcFile | null>(null);
  const [currentAction, setCurrentAction] = useState<TypePathUpdateData | null>(null);

  const handleChange = (value: string) => {
    setNewColor(value);
  };

  const handleRadioChange = (e: RadioChangeEvent) => {
    setRadioOpt(e.target.value);
  };

  const handleUpdate = (
    useTenantFk: number,
    updateData: ITenantPersonalizationUpdateData[]
  ) => {
    if (useTenantFk && updateData.length > 0) {
      const request: IUpdateTenantPersonalizationRequest = {
        TenantFk: useTenantFk,
        updateData,
      }
      dispatch(MultiDomainActions.updateTenantPersonalization(request));
    }
  }

  const handleUpdateImage = () => {
    setCurrentAction('LogoUrl');

    if (currentFile) {
      const request: IUploadPersonalization = {
        path: 'Logo',
        file: currentFile,
      }
      
      return dispatch(MultiDomainActions.uploadTenantPersonalization(request));
    }

    if (tenantFk) {
      const updateData: ITenantPersonalizationUpdateData[] = [];

      if (!isCustom && newImg) {
        updateData.push({
          path: "LogoUrl",
          op: 'replace',
          value: null,
        });
      }

      return handleUpdate(tenantFk, updateData);
    }
  };
  
  const handleUpdateColor = () => {
    if (tenantFk) {
      const updateData: ITenantPersonalizationUpdateData[] = [];
      updateData.push({
        path: "PrimaryColor",
        op: 'replace',
        value: newColor,
      });
  
      handleUpdate(tenantFk, updateData);
      setCurrentAction('PrimaryColor');
    }
  };
  
  const handleImageChange = (file: RcFile) => {
    setCurrentFile(file);
  };

  const handleUpdateCancelImage = () => {
    setCurrentFile(null);
    setRadioOpt(prev => {
      if (prev === 0 && newImg) {
        return 1;
      }

      return 0;
    });
  };

  const handleUpdateCancelColor = () => {
    setNewColor(theme.colors.primary.primary);
  };

  useEffect(() => {
    if (imgUploadState?.uploadCompleted) {
      setCurrentFile(null);
    }
  }, [imgUploadState?.uploadCompleted]);

  useEffect(() => {
    if (theme.colors.primary.primary) {
      setNewColor(theme.colors.primary.primary);
    }
    setNewImg(domain.LogoUrl);
    if (domain.LogoUrl) {
      setRadioOpt(1);
    }
  }, [theme.colors.primary.primary, domain.LogoUrl]);

  const progressPercent = useMemo(() => {
    if (imgUploadState?.sentSize && currentFile?.size) {
      const sentSize = imgUploadState?.sentSize || 0;
      const fileSize = currentFile?.size || 0;
      const total = sentSize > fileSize
        ? sentSize
        : fileSize;
      const sent = Math.min(sentSize, total);
      const percentage = Math.round((sent / total) * 100)
  
      return percentage;
    }

    return 0;
  }, [currentFile?.size, imgUploadState?.sentSize]);

  const isCustom = radioOpt === RadioOptions.Custom;
  const hasChangeColor = newColor !== theme.colors.primary.primary;
  const imgSending = imgUploadState?.isSending && imgUploadState.path === 'Logo';
  const logoActionLoading = currentAction === 'LogoUrl' && loading && !imgSending;
  const colorActionLoading = currentAction === 'PrimaryColor' && loading;

  return (
    <>
      <MainWrapperLeft>
        <div className="cardHeader">
          <CardTitle>{t('appearance.logoCardTitle')}</CardTitle>
          <AntDivider />
            <Radio.Group onChange={handleRadioChange} value={radioOpt}>
              <Radio value={RadioOptions.Coordly}>{t('appearance.radioCoordly')}</Radio>
              <Radio value={RadioOptions.Custom}>{t('appearance.radioCustom')}</Radio>
            </Radio.Group>
            {isCustom && (
              <>
                <ImgWrapper isProgress="true">
                  {(currentFile || newImg) && (
                    <img
                      src={loadImage(currentFile || newImg, true)}
                      alt={t('appearance.logoImgAlt')}
                    />
                  )}
                  {imgSending && (
                    <>
                      <Progress 
                        percent={progressPercent} 
                        size={[236, 5]} 
                        showInfo={false}
                        trailColor={theme.colors.surface.surface}
                        strokeColor={theme.colors.primary.primary}
                        className="progressBar"
                      />
                      <div className="progressMask" />
                    </>
                  )}
                </ImgWrapper>
                <InfoTextBold>{t('appearance.uploadFile')}</InfoTextBold>
                <UploadCropImg aspect={512 / 256} onChange={handleImageChange}/>
                <InfoText>{t('appearance.logoSizeHint')}</InfoText>
              </>
            )}
            {!isCustom && (
              <ImgWrapper>
                <img
                  src={loadImage(defaultInfoTheme.LogoUrl, true)}
                  alt={t('appearance.logoDefaultImgAlt')}
                />
              </ImgWrapper>
            )}
            {(isCustom || (!isCustom && newImg)) && (
              <>
                <AntDivider />
                <ButtonsAction 
                  disabledConfirm={isCustom && !currentFile}
                  loading={logoActionLoading}
                  onConfirm={handleUpdateImage}
                  onCancel={handleUpdateCancelImage}
                />
              </>
            )}
        </div>

        <div className="cardMain">
          <CardTitle>{t('appearance.colorsCardTitle')}</CardTitle>
          <AntDivider />
          <CardSubTitle>{t('appearance.primaryColor')}</CardSubTitle>
          <ColorPicker newColor={newColor} onChange={handleChange} />
          {hasChangeColor && (
            <ButtonsAction 
              loading={colorActionLoading}
              marginTop={20}  
              onConfirm={handleUpdateColor}
              onCancel={handleUpdateCancelColor}
            />
          )}
        </div>
      </MainWrapperLeft>

      <Divider />

      <MainWrapperRight> 
        <div className="fixedContainer">
          <SvgCard newColor={newColor}>
            <AppearanceBackgoundImage />
            {(currentFile || newImg) ? (
              <img
                src={loadImage(currentFile || newImg, true)}
                alt={t('appearance.logoImgAlt')}
                className="logo"
              />
            ) : (
              <img
                src={loadImage(defaultInfoTheme.LogoUrl, true)}
                alt={t('appearance.logoDefaultImgAlt')}
                className="logo"
              />
            )}
          </SvgCard>
        </div>
      </MainWrapperRight>
    </>
  )
}

export default AppearanceTab;
